import {Vector2} from 'three';

import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';
import {Debouncer} from 'external/dev_april_corgi+/js/common/debouncer';

// We clone events because some browsers don't treat events as immutable.
interface SavedEvent {
  clientX: number;
  clientY: number;
  position: Vector2;
}

interface Listener {
  down(
    position: Vector2,
    {
      contextual,
      ctrlKey,
      metaKey,
      shiftKey,
    }: {
      contextual: boolean;
      ctrlKey: boolean;
      metaKey: boolean;
      shiftKey: boolean;
    },
  ): void;
  click(
    position: Vector2,
    px: [x: number, y: number],
    {
      contextual,
      ctrlKey,
      metaKey,
      shiftKey,
      clickCount,
    }: {
      contextual: boolean;
      ctrlKey: boolean;
      metaKey: boolean;
      shiftKey: boolean;
      clickCount: number;
    },
  ): void;
  drag(position: Vector2, dx: number, dy: number): void;
  hover(position: Vector2, px: [x: number, y: number]): void;
  release(position: Vector2, px: [x: number, y: number]): void;
  zoom(amount: number, position: Vector2): void;
}

const DRAG_THRESHOLD2 = 3 * 3;

export class PointerInterpreter {
  private clickCount: number;
  private readonly clickCountResetter: Debouncer;
  private downEvent: SavedEvent | undefined;
  private pointers: Map<number, SavedEvent>;
  private viewport: DOMRect;

  constructor(
    target: Element,
    private readonly listener: Listener,
  ) {
    this.clickCount = 0;
    this.clickCountResetter = new Debouncer(/* ms= */ 500, () => {
      this.clickCount = 0;
    });
    this.downEvent = undefined;
    this.pointers = new Map();
    this.viewport = target.getBoundingClientRect();
  }

  down(e: PointerEvent): void {
    if ((e.target as Element | undefined)?.tagName === 'INPUT') {
      // Let the user focus the element
      return;
    } else if (document.activeElement?.tagName !== 'INPUT') {
      // Only preventDefault if the user has an irrelevant focus target. Otherwise we block them
      // from unfocusing.
      e.preventDefault();
    }

    const px = this.getPx(e);
    const position = this.updatePosition(px);
    const event = {
      clientX: px.clientX,
      clientY: px.clientY,
      position: position,
    };
    this.pointers.set(e.pointerId, event);
    if (this.pointers.size === 1) {
      this.downEvent = event;
    } else {
      this.downEvent = undefined;
    }
    this.listener.down(position, {
      contextual: e.buttons === 2,
      ctrlKey: e.ctrlKey,
      metaKey: e.metaKey,
      shiftKey: e.shiftKey,
    });
  }

  move(e: PointerEvent): void {
    e.preventDefault();
    const px = this.getPx(e);
    const position = this.updatePosition(px);

    if (!this.pointers.has(e.pointerId)) {
      this.listener.hover(position, [px.clientX, px.clientY]);
      return;
    }

    const event = {
      clientX: px.clientX,
      clientY: px.clientY,
      position,
    };

    if (this.downEvent) {
      const dx = px.clientX - this.downEvent.clientX;
      const dy = px.clientY - this.downEvent.clientY;
      if (
        e.buttons === 1 &&
        this.pointers.size === 1 &&
        dx * dx + dy * dy >= DRAG_THRESHOLD2
      ) {
        this.listener.drag(position, dx, -dy);
        this.downEvent = undefined;
      }
    } else {
      const last = checkExists(this.pointers.get(e.pointerId));
      const dx = px.clientX - last.clientX;
      const dy = px.clientY - last.clientY;
      this.listener.drag(position, dx, -dy);
    }

    this.pointers.set(e.pointerId, event);
  }

  up(e: PointerEvent): void {
    e.preventDefault();
    const px = this.getPx(e);
    const position = this.updatePosition(px);

    if (this.downEvent) {
      // When considering a double click, make sure the pointer hasn't moved very far
      const dx = px.clientX - this.downEvent.clientX;
      const dy = px.clientY - this.downEvent.clientY;
      if (dx * dx + dy * dy < DRAG_THRESHOLD2) {
        this.clickCount += 1;
        this.clickCountResetter.trigger();
      } else {
        this.clickCount = 1;
      }
      this.listener.click(position, [px.clientX, px.clientY], {
        contextual: e.button === 2,
        ctrlKey: e.ctrlKey,
        metaKey: e.metaKey,
        shiftKey: e.shiftKey,
        clickCount: this.clickCount,
      });
    } else if (this.pointers.size > 0) {
      this.listener.release(position, [px.clientX, px.clientY]);
    }

    this.pointers.delete(e.pointerId);
  }

  resize(viewport: DOMRect): void {
    this.viewport = viewport;
  }

  wheel(e: WheelEvent): void {
    this.listener.zoom(-0.01 * e.deltaY, this.updatePosition(e));
    e.preventDefault();
  }

  private getPx(e: PointerEvent | WheelEvent): {
    clientX: number;
    clientY: number;
  } {
    return {
      clientX: e.clientX - this.viewport.x,
      clientY: e.clientY - this.viewport.y,
    };
  }

  private updatePosition(e: {clientX: number; clientY: number}): Vector2 {
    return new Vector2(
      (e.clientX / this.viewport.width) * 2 - 1,
      (-e.clientY / this.viewport.height) * 2 + 1,
    );
  }
}
